/**
 * A fixture's desk, full window: this page's own header, and the app that
 * scores or records the fixture in a frame below it.
 *
 * Which apps can open the fixture comes from GET /apps/choices. The sport's
 * scoring desk opens by default; when Results is offered too, the person can
 * switch to it. `?app=<code>&workflow=<code>` keeps the pick on reload.
 *
 * The header and the "not ready to score" check come from
 * GET /scoring/fixtures/{id}/console. Everything the app reads, sends and saves
 * goes over the bridge, answered by AppFrame.
 */

import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { createHttpApi, type AppChoice } from "@fanos/omnium-bridge";

import { AppFrame, FrameState, LiveDot } from "@/components/bridge/AppFrame";
import { Button, LinkButton } from "@/components/kit";
import { apiFetch, getToken } from "@/lib/api/auth";
import { getAdminApiBaseUrl } from "@/lib/api/config";
import { choiceKey, pickChoice, problemFor, type Live } from "@/lib/bridge/frame";
import { useApi } from "@/spa/useApi";

import styles from "./ScoringConsolePage.module.css";

type ConsoleInfo = {
  fixtureId: string;
  fixtureCode: string;
  fixtureName: string | null;
  sportCode: string;
  status: string;
  /** False when the desk cannot open yet — each side needs its squad. */
  ready: boolean;
  blockers: string[];
};

async function loadInfo(id: string): Promise<ConsoleInfo> {
  const response = await apiFetch(
    `${getAdminApiBaseUrl()}/scoring/fixtures/${encodeURIComponent(id)}/console`,
  );
  const body = (await response.json().catch(() => ({}))) as { detail?: unknown };
  if (!response.ok) {
    throw new Error(typeof body.detail === "string" ? body.detail : `omnium answered ${response.status}`);
  }
  return body as unknown as ConsoleInfo;
}

/** A desk is named by its app; Results and other shared workflows by the workflow. */
function choiceLabel(choice: AppChoice): string {
  return choice.workflow.mode === "apply" ? choice.workflow.name : choice.app.name;
}

export function ScoringConsolePage() {
  const { id = "" } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();

  const info = useApi(() => loadInfo(id), [id]);
  const choices = useApi(
    () =>
      createHttpApi({ baseUrl: getAdminApiBaseUrl(), token: getToken }).choices({
        kind: "fixture",
        id,
      }),
    [id],
  );
  // The fixture's status moves with the commands the app sends.
  const [seen, setSeen] = useState<{ id: string; status: string } | null>(null);

  const list = choices.data ?? [];
  const chosen = pickChoice(list, "score", {
    app: params.get("app"),
    workflow: params.get("workflow"),
  });
  const results = list.find((choice) => choice.workflow.mode === "apply");
  const status = (seen?.id === id ? seen.status : null) ?? info.data?.status ?? null;
  const title = info.data ? (info.data.fixtureName ?? info.data.fixtureCode) : "Fixture";

  const pick = (choice: AppChoice) =>
    setParams({ app: choice.app.code, workflow: choice.workflow.code }, { replace: true });

  const header = (live?: Live) => {
    const tone = status === "COMPLETED" ? "done" : status === "LIVE" ? "live" : undefined;
    return (
      <header className={styles.bar}>
        <Link className={styles.back} to={`/fixtures/${id}`}>
          ← fixture
        </Link>
        <h1 className={styles.title}>{title}</h1>
        {chosen && <span className={styles.surface}>via {chosen.app.name}</span>}
        {list.length > 1 && chosen && (
          <div className={styles.switch} role="radiogroup" aria-label="Open with">
            {list.map((choice) => (
              <button
                key={choiceKey(choice)}
                type="button"
                role="radio"
                aria-checked={choiceKey(choice) === choiceKey(chosen)}
                className={styles.switchOption}
                onClick={() => pick(choice)}
              >
                {choiceLabel(choice)}
              </button>
            ))}
          </div>
        )}
        <span className={styles.push} />
        {live && <LiveDot live={live} />}
        {status && (
          <span className={styles.status} data-tone={tone}>
            {status}
          </span>
        )}
      </header>
    );
  };

  let body;
  if (choices.error) {
    const problem = problemFor(choices.cause);
    body = (
      <>
        {header()}
        <FrameState title={problem.title} note={problem.note}>
          {problem.retry && (
            <Button tone="primary" onClick={choices.reload}>
              Try again
            </Button>
          )}
        </FrameState>
      </>
    );
  } else if (!choices.data) {
    body = (
      <>
        {header()}
        <FrameState title="Opening…" />
      </>
    );
  } else if (!chosen) {
    body = (
      <>
        {header()}
        <FrameState
          title="No app is set up to open this fixture"
          note="Map a scoring desk to this sport's workflow, or an app to Results, then open this page again."
        >
          <LinkButton to="/workflows" tone="primary">
            Open workflows
          </LinkButton>
          <LinkButton to={`/fixtures/${id}`}>Back to the fixture</LinkButton>
        </FrameState>
      </>
    );
  } else if (chosen.workflow.mode !== "apply" && info.data && !info.data.ready) {
    body = (
      <>
        {header()}
        <FrameState
          title="This fixture is not ready to score"
          note="A scoring desk needs each side's squad before it can open. Name the line-ups on the fixture, then open the desk again."
        >
          {results && (
            <Button tone="primary" onClick={() => pick(results)}>
              Record the result with {results.workflow.name}
            </Button>
          )}
          <LinkButton to={`/fixtures/${id}`}>Back to the fixture</LinkButton>
        </FrameState>
        {info.data.blockers.length > 0 && (
          <ul className={styles.blockers}>
            {info.data.blockers.map((blocker) => (
              <li key={blocker}>{blocker}</li>
            ))}
          </ul>
        )}
      </>
    );
  } else {
    body = (
      <AppFrame
        key={choiceKey(chosen)}
        app={chosen.app.code}
        workflow={chosen.workflow.code}
        subject={{ kind: "fixture", id }}
        bar={({ live }) => header(live)}
        onCommand={(result) => {
          if (result.fixtureStatus) setSeen({ id, status: result.fixtureStatus });
        }}
      />
    );
  }

  return <div className={styles.shell}>{body}</div>;
}
